import type { ReactNode, FC } from "react";
import { useState, useRef, useEffect, Fragment, useDeferredValue } from "react";
import {
  Button,
  Divider,
  Stack,
  Typography,
  Box,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  type Theme,
} from "@mui/material";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  Evaluation,
  GetPartnerByIdResponse,
  SectionEvaluation,
  UpdateSectionEvaluationPayload,
  ComplianceSection,
  ComplianceWithAnswers,
} from "../../api/partners";
import type { PartnerSecurityTrialRequestStatus } from "../../api/userInfo";
import {
  getFormByComplianceQueryOptions,
  updateComplianceAnswersMutationOptions,
  complianceByIdQueryOptions,
  updateSectionEvaluationMutationOptions,
  UpdateSectionEvaluationPayloadSchema,
  EvaluationSchema,
  requestSecurityTrialMutationOptions,
  createOrUpdateEvaluationCommentMutationOptions,
  partnerByIdQueryOptions,
  markPartnerComplianceAsDoneMutationOptions,
} from "../../api/partners";
import type { Section } from "../../dynamicForm";
import { DynamicForm } from "../../dynamicForm";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { trackEvent } from "../../shared/Snowplow";
import * as FormDialog from "../../shared/FormDialog";
import { appConfig } from "../../appConfig";
import { TextFieldActionButtons } from "../../shared/TextFieldActionButtons";
import { PartnerDrawerHeader } from "./PartnerDrawerHeader";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { ComplianceHeaderMeta } from "./ComplianceHeaderMeta";
import dayjs from "dayjs";

import { ComplianceLifecycleInfo, isTimeToReview, SubTitle, Title } from "./ComplianceLifecycleInfo";

export const ComplianceSubDrawer = () => {
  const partnerId = DynamicGrid.useSelectedRowId();
  const partnerByIdQuery = useQuery(partnerByIdQueryOptions(partnerId));

  if (!partnerByIdQuery.data)
    return (
      <DynamicGrid.Drawer>
        <Drawer.Loading />
      </DynamicGrid.Drawer>
    );

  return <ComplianceSubDrawerInner compliance={partnerByIdQuery.data.compliance} partner={partnerByIdQuery.data} />;
};

type ComplianceSubDrawerInnerProps = {
  compliance: GetPartnerByIdResponse["compliance"];
  partner: GetPartnerByIdResponse;
};

const ComplianceSubDrawerInner: FC<ComplianceSubDrawerInnerProps> = ({ compliance, partner }) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const currentOrganisation = useCurrentOrganisation();
  const formByIdQuery = useQuery(getFormByComplianceQueryOptions());
  const complianceByIdQuery = useQuery(complianceByIdQueryOptions(compliance.id));
  const updateAnswersMutation = useMutation(updateComplianceAnswersMutationOptions());
  const hasPartnerSecurityAndPrivacyProfileEnabled = currentOrganisation.partnerSecurityAndPrivacyProfileEnabled;

  const complianceData = complianceByIdQuery.data;
  const isPartnerArchived = partner.status === "archived";
  const isSecurityProfileGated =
    !hasPartnerSecurityAndPrivacyProfileEnabled && currentOrganisation.partnerSecurityTrialRequestStatus !== "active";

  return (
    <DynamicGrid.Drawer sx={{ pb: 0 }}>
      {(() => {
        if (!complianceData || !formByIdQuery.data) return <Drawer.Loading />;

        return (
          <DynamicForm.Provider
            onSave={answer =>
              updateAnswersMutation.mutateAsync({
                complianceId: complianceData.id,
                answers: [answer],
              })
            }
            isSaving={updateAnswersMutation.isPending}
            isSaveError={updateAnswersMutation.isError}
            key={complianceData.id}
            answers={complianceData.answers}
            isReadOnly={Boolean(complianceData.external) || isPartnerArchived}
          >
            {({ formState }) => (
              <>
                <PartnerDrawerHeader
                  status={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                  partner={partner}
                  onClose={() => navigate(paths.partnersGrid())}
                />
                <Drawer.SubDrawerHeader
                  onBackClick={() => navigate(paths.partnersInline(partner.id))}
                  title={t("Compliance.SubDrawer.Header.Title")}
                  customSlot={
                    !isPartnerArchived &&
                    !hasPartnerSecurityAndPrivacyProfileEnabled &&
                    currentOrganisation.partnerSecurityTrialRequestStatus !== "subscribed" && (
                      <Box sx={{ pl: 6, pr: 4 }}>
                        <TrialStatusInline trialStatus={currentOrganisation.partnerSecurityTrialRequestStatus} />
                      </Box>
                    )
                  }
                  sx={{ alignItems: "flex-start", py: 2 }}
                >
                  <ComplianceHeaderMeta complianceData={complianceData} isPartnerArchived={isPartnerArchived} />
                </Drawer.SubDrawerHeader>
                <Drawer.Content sx={{ py: 0 }}>
                  <Drawer.Divider />

                  {isPartnerArchived && <ArchivedPartnerInfo />}

                  <BlurOverlay showBlur={isPartnerArchived || isSecurityProfileGated}>
                    <ComplianceLifecycleInfo partnerName={partner.name} compliance={complianceData} />

                    <Drawer.Divider />
                    <Box sx={{ pt: 5 }}>
                      <DynamicForm.InlineForm
                        variant="secondary"
                        renderSlots={{
                          afterSection: section => {
                            return (
                              <Box sx={{ pb: 10 }}>
                                <SectionEvaluation
                                  section={section}
                                  sectionEvaluation={complianceData.evaluation.sectionEvaluations.find(
                                    s => s.sectionId === section.id
                                  )}
                                  complianceId={complianceData.id}
                                />
                              </Box>
                            );
                          },
                        }}
                        id={complianceData.id}
                        form={formByIdQuery.data}
                        previousAnswersInfo={
                          complianceData.external ? complianceData.external.answerSnapshotsChangedSinceReviewedAt : []
                        }
                      />
                    </Box>

                    <SectionEvaluationSummary
                      isTimeForReview={(() => {
                        if (dayjs(complianceData.nextAudit).isBefore(dayjs())) return true;

                        const answeredAt = complianceData.external
                          ? complianceData.external.answeredAt
                          : complianceData.manual.answerUpdatedAt;
                        return isTimeToReview(answeredAt, complianceData.evaluation.reviewed?.at);
                      })()}
                      sections={formByIdQuery.data.sections}
                      evaluation={complianceData.evaluation}
                      compliance={complianceData}
                    />
                  </BlurOverlay>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

type TrialStatusInlineProps = {
  trialStatus: PartnerSecurityTrialRequestStatus;
};

const TrialStatusInline: FC<TrialStatusInlineProps> = ({ trialStatus }) => {
  const { t } = useTranslation();
  const currentOrganisation = useCurrentOrganisation();
  const requestSecurityTrialMutation = useMutation(requestSecurityTrialMutationOptions());

  const upgradeUrl = currentOrganisation.lang === "no" ? appConfig.BOOK_DEMO_URL.no : appConfig.BOOK_DEMO_URL.se;

  if (trialStatus === "none") {
    return (
      <Stack sx={{ gap: 1, alignItems: "flex-start" }}>
        <Button
          size="small"
          color="primary"
          onClick={() => requestSecurityTrialMutation.mutate()}
          disabled={requestSecurityTrialMutation.isPending}
        >
          {t("Partner.SecurityAndPrivacyProfile.StartFreeTrial")}
        </Button>
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12) }}>
          {t("Partner.SecurityAndPrivacyProfile.ExploreInfo")}
        </Typography>
      </Stack>
    );
  }

  if (trialStatus === "subscribed") return null;

  const titleColor = (theme: Theme) =>
    trialStatus === "active" ? theme.palette.brandGreen[600] : theme.palette.text.primary;

  return (
    <Stack sx={{ alignItems: "flex-start" }}>
      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600, color: titleColor }}>
        {(() => {
          switch (trialStatus) {
            case "active":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.active.Title");
            case "pending":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.pending.Title");
            case "failed":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.failed.Title");
            case "cancelled":
            case "terminated":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.terminated.Title");
          }
        })()}
      </Typography>
      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}>
        {(() => {
          switch (trialStatus) {
            case "active":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.active.Description");
            case "pending":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.pending.Description");
            case "failed":
              return t("Partner.SecurityAndPrivacyProfile.TrialStatus.failed.Description");
            case "cancelled":
            case "terminated":
              return (
                <Translate
                  translationKey="Partner.SecurityAndPrivacyProfile.TrialStatus.terminated.Description"
                  values={{
                    upgradeLink: (
                      <Link
                        rel="noopener noreferrer"
                        target="_blank"
                        href={upgradeUrl}
                        onClick={() =>
                          trackEvent.clickPartnerSecurityAndPrivacyProfileUpgradeLink(
                            currentOrganisation.name,
                            currentOrganisation.hubspotId
                          )
                        }
                      >
                        {t("Partner.SecurityAndPrivacyProfile.UpgradeNow")}
                      </Link>
                    ),
                  }}
                />
              );
          }
        })()}
      </Typography>
    </Stack>
  );
};

const ArchivedPartnerInfo = () => {
  const { t } = useTranslation();

  return (
    <>
      <Stack sx={{ py: 4, gap: 0.5 }}>
        <Title>{t("Compliance.Subdrawer.Archived.Title")}</Title>
        <SubTitle>{t("Compliance.Subdrawer.Archived.Description")}</SubTitle>
      </Stack>
      <Drawer.Divider />
    </>
  );
};

const BlurOverlay: FC<{ children: ReactNode; showBlur: boolean }> = ({ children, showBlur }) => {
  if (!showBlur) return children;

  return (
    <Box
      inert
      sx={{
        "all": "unset",
        "position": "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: theme => theme.palette.background.paper,
          opacity: 0.5,
        },
      }}
    >
      {children}
    </Box>
  );
};

type SectionEvaluationProps = {
  section: Section;
  sectionEvaluation?: SectionEvaluation;
  complianceId: string;
};

const SectionEvaluation: FC<SectionEvaluationProps> = ({ section, sectionEvaluation, complianceId }) => {
  const { t } = useTranslation();
  const updateEvaluationMutation = useMutation(
    updateSectionEvaluationMutationOptions({ complianceId, sectionId: section.id })
  );
  const updateCommentMutation = useMutation(
    updateSectionEvaluationMutationOptions({ complianceId, sectionId: section.id })
  );

  const [assessment, setAssessment] = useState(sectionEvaluation?.assessment);

  const constructPayload = (updated: Partial<UpdateSectionEvaluationPayload>): UpdateSectionEvaluationPayload => {
    return { comment: sectionEvaluation?.comment, assessment, ...updated };
  };

  return (
    <Stack
      sx={{
        gap: 6,
        backgroundColor: theme => theme.palette.grey[100],
        padding: 6,
        border: "1px solid",
        borderColor: theme => theme.palette.grey[300],
        borderLeftWidth: 4,
        ...(assessment && { borderLeftColor: evaluationColor(assessment) }),
      }}
    >
      <Typography variant="h3" sx={{ fontSize: theme => theme.typography.pxToRem(16), fontWeight: "600" }}>
        {t("Compliance.Subdrawer.Evaluation.SectionTitle", { sectionName: section.name })}
      </Typography>
      <Stack>
        <FormDialog.Field
          errorMessage={updateEvaluationMutation.isError && t("Compliance.Subdrawer.Evaluation.Error.UpdateEvaluation")}
        >
          <FormDialog.Label htmlFor="assessment">
            {t("Compliance.Subdrawer.Evaluation.AssessmentLabel")}
          </FormDialog.Label>

          <FormControl sx={{ width: "280px" }}>
            <Select
              value={assessment ?? ""}
              onChange={e => {
                const newAssessment = UpdateSectionEvaluationPayloadSchema.shape.assessment.parse(e.target.value);
                setAssessment(newAssessment);
                updateEvaluationMutation.mutate(constructPayload({ assessment: newAssessment }), {
                  onError: () => setAssessment(assessment),
                });
              }}
              id="assessment"
              size="small"
              displayEmpty
              renderValue={option => {
                if (!option) {
                  return (
                    <Typography
                      sx={{
                        fontSize: theme => theme.typography.pxToRem(14),
                        color: theme => theme.palette.text.tertiary,
                        opacity: 1,
                      }}
                    >
                      {t("Compliance.Subdrawer.Evaluation.SelectPlaceholder")}
                    </Typography>
                  );
                }

                return (
                  <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                    <EvaluationDot evaluation={option} />
                    {t(`Compliance.Subdrawer.Evaluation.${option}`)}
                  </Stack>
                );
              }}
            >
              {EvaluationSchema.options
                .map(option => (
                  <MenuItem key={option} value={option}>
                    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                      <EvaluationDot evaluation={option} />
                      {t(`Compliance.Subdrawer.Evaluation.${option}`)}
                    </Stack>
                  </MenuItem>
                ))
                .reverse()}
            </Select>
          </FormControl>
        </FormDialog.Field>
        <FormDialog.Field
          errorMessage={updateCommentMutation.isError && t("Compliance.Subdrawer.Evaluation.Error.UpdateComment")}
        >
          <FormDialog.Label htmlFor={`${section.id}-comment`}>
            {t("Compliance.Subdrawer.Evaluation.CommentLabel")}
          </FormDialog.Label>
          <EvaluationTextInput
            id={`${section.id}-comment`}
            defaultValue={sectionEvaluation?.comment}
            onCancel={() => updateCommentMutation.reset()}
            onSave={value => updateCommentMutation.mutateAsync(constructPayload({ comment: value }))}
          />
        </FormDialog.Field>
      </Stack>
    </Stack>
  );
};

const evaluationColor = (evaluation: Evaluation) => (theme: Theme) => {
  const colors = {
    approved: theme.palette.brandGreen[500],
    approvedWithConditions: theme.palette.brandOrange[400],
    rejected: theme.palette.brandRed[500],
  };
  return colors[evaluation];
};

export const EvaluationDot: FC<{ evaluation: Evaluation }> = ({ evaluation }) => {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        backgroundColor: evaluationColor(evaluation),
        borderRadius: "50%",
        marginRight: 0.5,
      }}
    />
  );
};

type EvaluationTextInputProps = {
  defaultValue: string | null | undefined;
  onSave: (value: string) => Promise<unknown>;
  onCancel: () => void;
  rows?: number;
  id: string;
};

const EvaluationTextInput: FC<EvaluationTextInputProps> = ({ defaultValue, onSave, onCancel, rows = 3, id }) => {
  const { t } = useTranslation();
  const [inputState, setInputState] = useState<"editing" | "idle">("idle");
  const [value, setValue] = useState(defaultValue);
  const mutation = useMutation({ mutationFn: onSave, onSuccess: () => setInputState("idle") });

  const handleCancel = () => {
    onCancel();
    setInputState("idle");
    setValue(defaultValue);
  };

  return (
    <Box
      onBlur={TextFieldActionButtons.onTextFieldWrapperBlur({
        isDirty: value !== defaultValue,
        onCancel: handleCancel,
      })}
      sx={{ width: "100%" }}
    >
      <TextField
        id={id}
        size="small"
        value={value ?? ""}
        onChange={e => setValue(e.target.value)}
        multiline
        onFocus={() => setInputState("editing")}
        fullWidth
        minRows={rows}
        placeholder={t("Compliance.Subdrawer.Evaluation.CommentPlaceholder")}
        sx={{
          ...TextFieldActionButtons.TextFieldSx,
          "& .MuiInputBase-input::placeholder": { color: theme => theme.palette.text.tertiary, opacity: 1 },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <TextFieldActionButtons
                isDirty={value !== defaultValue}
                isEditing={inputState === "editing"}
                isSaving={mutation.isPending}
                onSave={() => mutation.mutate(value ?? "", { onSuccess: () => setInputState("idle") })}
                onCancel={() => {
                  onCancel();
                  setInputState("idle");
                  setValue(defaultValue);
                }}
              />
            ),
          },
        }}
      />
    </Box>
  );
};

type SectionEvaluationSummaryProps = {
  isTimeForReview?: boolean;
  sections: ComplianceSection[];
  evaluation: ComplianceWithAnswers["evaluation"];
  compliance: ComplianceWithAnswers;
};

const SectionEvaluationSummary: FC<SectionEvaluationSummaryProps> = ({
  isTimeForReview,
  sections,
  evaluation,
  compliance,
}) => {
  const { t } = useTranslation();

  const deferredIsTimeForReview = useDeferredValue(isTimeForReview);

  const createOrUpdateEvaluationCommentMutation = useMutation(
    createOrUpdateEvaluationCommentMutationOptions({ complianceId: compliance.id })
  );

  return (
    <>
      <OverallAssessment
        isTimeForReview={deferredIsTimeForReview}
        complianceId={compliance.id}
        evaluation={evaluation.assessment}
      />
      <Stack sx={{ pt: 2 }}>
        <FormDialog.Field
          errorMessage={
            createOrUpdateEvaluationCommentMutation.isError && t("Compliance.Subdrawer.Evaluation.Error.UpdateComment")
          }
        >
          <Box
            component="label"
            sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600, mb: 1 }}
            htmlFor="evaluation-summary-comment"
          >
            {t("Compliance.Subdrawer.EvaluationSummary.OverallAssessmentComment")}
          </Box>
          <EvaluationTextInput
            id="evaluation-summary-comment"
            defaultValue={evaluation.comment}
            rows={7}
            onCancel={() => createOrUpdateEvaluationCommentMutation.reset()}
            onSave={value => createOrUpdateEvaluationCommentMutation.mutateAsync({ comment: value })}
          />
        </FormDialog.Field>
      </Stack>
      <Stack sx={{ pb: 6, pt: 3 }}>
        <Divider />
        <Stack>
          {sections.map(section => {
            const sectionEvaluation = evaluation.sectionEvaluations.find(
              evaluation => evaluation.sectionId === section.id
            );
            const assessment = sectionEvaluation?.assessment;

            return (
              <Stack
                key={section.id}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  gap: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: theme => theme.palette.divider,
                }}
              >
                <Typography sx={{ flex: 1, fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14) }}>
                  {section.name}
                </Typography>
                <Stack direction="row" sx={{ alignItems: "center", gap: 1, width: "172px" }}>
                  {assessment ? (
                    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                      <EvaluationDot evaluation={assessment} />
                      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), textWrap: "nowrap" }}>
                        {t(`Compliance.Subdrawer.Evaluation.${assessment}`)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: theme => theme.typography.pxToRem(14),
                        color: theme => theme.palette.text.tertiary,
                      }}
                    >
                      -
                    </Typography>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </>
  );
};

type OverallAssessmentProps = {
  isTimeForReview?: boolean;
  complianceId: string;
  evaluation: ComplianceWithAnswers["evaluation"]["assessment"];
};

const OverallAssessment: FC<OverallAssessmentProps> = ({ isTimeForReview, complianceId, evaluation }) => {
  const { t } = useTranslation();
  const triggerStickyContentReachedEndRef = useRef<HTMLDivElement>(null);
  const [hasStickyContentReachedEnd, setHasStickyContentReachedEnd] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<Evaluation | undefined>(evaluation);
  const [markAsDoneError, setMarkAsDoneError] = useState<string | undefined>(undefined);

  const markAsDoneMutation = useMutation(markPartnerComplianceAsDoneMutationOptions({ complianceId }));

  useEffect(() => {
    const triggerStickyContentReachedEnd = triggerStickyContentReachedEndRef.current;

    if (!triggerStickyContentReachedEnd) return;

    const observer = new IntersectionObserver(([entry]) => {
      setHasStickyContentReachedEnd(entry.isIntersecting);
    });

    observer.observe(triggerStickyContentReachedEnd);

    return () => observer.disconnect();
  }, []);

  const handleMarkAsDone = () => {
    if (!selectedAssessment) {
      setMarkAsDoneError(t("Compliance.Subdrawer.Evaluation.OverallAssessmentRequired"));
      return;
    }

    const previousAssessment = evaluation;
    setMarkAsDoneError(undefined);
    markAsDoneMutation.mutate(
      { overallAssessment: selectedAssessment },
      {
        onError: () => {
          setSelectedAssessment(previousAssessment);
          setMarkAsDoneError(t("Compliance.Subdrawer.Evaluation.Error.MarkAsDone"));
        },
      }
    );
  };

  const position = isTimeForReview ? "sticky" : undefined;

  return (
    <Fragment>
      <Stack
        sx={{
          position,
          pb: OVERALL_ASSESSMENT.PADDING_TO_DIVIDER,
          bottom: theme => `calc( ${OVERALL_ASSESSMENT.HEIGHT} - ${OVERALL_ASSESSMENT.PADDING_TO_DIVIDER(theme)} )`,
          backgroundColor: theme => theme.palette.background.paper,
        }}
      >
        <Drawer.Divider />
      </Stack>
      <Stack
        sx={{
          position,
          justifyContent: "center",
          height: OVERALL_ASSESSMENT.HEIGHT,
          bottom: 0,
          backgroundColor: theme => theme.palette.background.paper,
        }}
      >
        <Typography
          key={position}
          sx={[
            {
              fontSize: theme => theme.typography.pxToRem(16),
              fontWeight: 600,
            },
            Boolean(isTimeForReview) && {
              opacity: 0,
              ...(!selectedAssessment && {
                transform: `translateY(${OVERALL_ASSESSMENT.SCROLL_ANIMATION.distance})`,
                transition: OVERALL_ASSESSMENT.SCROLL_ANIMATION.transition,
              }),
            },
            hasStickyContentReachedEnd && { opacity: 1, transform: "translateY(0)" },
          ]}
        >
          {t("Compliance.Subdrawer.Evaluation.OverallAssessment")}
        </Typography>

        <Stack direction="row" sx={{ pt: 2, pb: 6, gap: 3, alignItems: "center" }}>
          <Select
            value={selectedAssessment ?? ""}
            onChange={e => {
              const value = EvaluationSchema.parse(e.target.value);
              setSelectedAssessment(value);
              setMarkAsDoneError(undefined);
              markAsDoneMutation.reset();
            }}
            displayEmpty
            id="overall-assessment"
            sx={{ flex: 1, maxWidth: "400px" }}
            renderValue={option =>
              !option && isTimeForReview ? (
                <Typography
                  sx={[
                    {
                      color: theme => theme.palette.text.tertiary,
                      opacity: 0.6,
                      fontSize: theme => theme.typography.pxToRem(14),
                      transition: OVERALL_ASSESSMENT.SCROLL_ANIMATION.transition,
                    },
                    hasStickyContentReachedEnd && {
                      opacity: 0,
                      transform: `translateY(-${OVERALL_ASSESSMENT.SCROLL_ANIMATION.distance})`,
                    },
                  ]}
                >
                  {t("Compliance.Subdrawer.Evaluation.OverallAssessment")}
                </Typography>
              ) : option ? (
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <EvaluationDot evaluation={option} />
                  {t(`Compliance.Subdrawer.Evaluation.${option}`)}
                </Stack>
              ) : (
                <Typography
                  sx={{
                    fontSize: theme => theme.typography.pxToRem(14),
                    color: theme => theme.palette.text.tertiary,
                    opacity: 1,
                  }}
                >
                  {t("Compliance.Subdrawer.Evaluation.OverallAssessment")}
                </Typography>
              )
            }
          >
            {EvaluationSchema.options.map(option => (
              <MenuItem key={option} value={option}>
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <EvaluationDot evaluation={option} />
                  {t(`Compliance.Subdrawer.Evaluation.${option}`)}
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMarkAsDone}
            loading={markAsDoneMutation.isPending}
            disabled={markAsDoneMutation.isPending}
          >
            {t("Compliance.Subdrawer.Evaluation.MarkAsDone")}
          </Button>
          <FormDialog.FormError isError={Boolean(markAsDoneError)} errorMessage={markAsDoneError} />
        </Stack>
      </Stack>
      <Box ref={triggerStickyContentReachedEndRef} />
    </Fragment>
  );
};

const OVERALL_ASSESSMENT = {
  HEIGHT: "80px",
  SCROLL_ANIMATION: { distance: "40px", transition: `all 100ms linear` },
  PADDING_TO_DIVIDER: (theme: Theme) => theme.spacing(10),
};
